import React, { useState } from 'react';
import { Tabs, Card, Typography, Tag, Spin, Alert, Select, Statistic, Row, Col } from 'antd';
import { RobotOutlined, HistoryOutlined, BarChartOutlined, SettingOutlined, CodeOutlined, DatabaseOutlined, BranchesOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { API } from '@/services/api.service';
import PromptEditorTab from './agent-config/PromptEditorTab';
import ModelSettingsTab from './agent-config/ModelSettingsTab';
import VersionHistoryTab from './agent-config/VersionHistoryTab';
import PerformanceMetricsTab from './agent-config/PerformanceMetricsTab';
import KnowledgeBaseTab from './agent-config/KnowledgeBaseTab';
import PipelineBuilderTab from './agent-config/PipelineBuilderTab';

const { Text, Title } = Typography;

export default function AgentConfigPanel() {
  const { t } = useTranslation(['tables']);
  const [selectedType, setSelectedType] = useState('PCCC_PARSER');
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [activeTab, setActiveTab] = useState('prompt');

  const pa = (key) => t(`payrollAutomation.agentConfig.${key}`, { ns: 'tables', defaultValue: key });

  // Fetch all configurations for selected type
  const { data: configurationsData, isLoading: configsLoading } = useQuery({
    queryKey: ['agent-configs', selectedType],
    queryFn: () => API.agentConfig.getAgentConfigurations({ type: selectedType }),
  });

  const configurations = configurationsData?.data || [];

  // Get active configuration
  const { data: activeConfigData } = useQuery({
    queryKey: ['active-agent-config', selectedType],
    queryFn: () => API.agentConfig.getActiveConfiguration(selectedType),
    enabled: !!selectedType,
  });

  const activeConfig = activeConfigData?.data;

  // Get selected configuration details
  const { data: selectedConfigData, isLoading: detailLoading } = useQuery({
    queryKey: ['agent-config-detail', selectedConfigId],
    queryFn: () => API.agentConfig.getAgentConfigurationById(selectedConfigId),
    enabled: !!selectedConfigId,
  });

  const selectedConfig = selectedConfigData?.data;

  // Auto-select active config if none selected
  React.useEffect(() => {
    if (activeConfig && !selectedConfigId) {
      setSelectedConfigId(activeConfig._id);
    }
  }, [activeConfig, selectedConfigId]);

  const isPipelineMode = selectedConfig?.mode === 'pipeline';

  const tabs = [
    // Prompt Editor - only in single mode
    ...(!isPipelineMode ? [{
      key: 'prompt',
      label: (
        <span>
          <CodeOutlined /> {pa('promptEditor')}
        </span>
      ),
      children: (
        <PromptEditorTab
          config={selectedConfig}
          type={selectedType}
        />
      ),
    }] : []),
    {
      key: 'pipeline',
      label: (
        <span>
          <BranchesOutlined /> {pa('pipelineBuilder') || 'Pipeline Builder'}
        </span>
      ),
      children: (
        <PipelineBuilderTab
          config={selectedConfig}
        />
      ),
    },
    {
      key: 'knowledge',
      label: (
        <span>
          <DatabaseOutlined /> {pa('knowledgeBase') || 'Knowledge Base'}
        </span>
      ),
      children: (
        <KnowledgeBaseTab
          config={selectedConfig}
        />
      ),
    },
    // Model Settings - only in single mode
    ...(!isPipelineMode ? [{
      key: 'model',
      label: (
        <span>
          <SettingOutlined /> {pa('modelSettings')}
        </span>
      ),
      children: (
        <ModelSettingsTab
          config={selectedConfig}
        />
      ),
    }] : []),
    {
      key: 'history',
      label: (
        <span>
          <HistoryOutlined /> {pa('versionHistory')}
        </span>
      ),
      children: (
        <VersionHistoryTab
          configurations={configurations}
          selectedType={selectedType}
        />
      ),
    },
    {
      key: 'metrics',
      label: (
        <span>
          <BarChartOutlined /> {pa('performanceMetrics')}
        </span>
      ),
      children: (
        <PerformanceMetricsTab
          config={selectedConfig}
        />
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <RobotOutlined /> {pa('aiAgentConfiguration')}
        </Title>
        <Text type="secondary">{pa('manageClaudeAgent')}</Text>
      </div>

      {/* Type Selector & Active Config Info */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={24} align="middle">
          <Col span={8}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>{pa('parserType')}</Text>
            </div>
            <Select
              value={selectedType}
              onChange={setSelectedType}
              style={{ width: '100%' }}
              options={[
                { value: 'PCCC_PARSER', label: 'PCCC (China Power)' },
                { value: 'DREAM_POINT_PARSER', label: 'Dream Point Distribution' },
              ]}
            />
          </Col>

          <Col span={8}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>{pa('activeConfiguration')}</Text>
            </div>
            <Select
              value={selectedConfigId}
              onChange={setSelectedConfigId}
              style={{ width: '100%' }}
              loading={configsLoading}
              options={configurations.map((config) => ({
                value: config._id,
                label: (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{config.name} (v{config.version})</span>
                    {config.active && <Tag color="green">Active</Tag>}
                  </div>
                ),
              }))}
              notFoundContent={configsLoading ? <Spin size="small" /> : pa('noConfigurations')}
            />
          </Col>

          {activeConfig && (
            <>
              <Col span={4}>
                <Statistic
                  title={pa('avgConfidence')}
                  value={activeConfig.metrics?.avgConfidence || 0}
                  suffix="%"
                  valueStyle={{ fontSize: 20 }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title={pa('successRate')}
                  value={activeConfig.successRate || 0}
                  suffix="%"
                  valueStyle={{ fontSize: 20 }}
                />
              </Col>
            </>
          )}
        </Row>
      </Card>

      {/* Config Details or Loading */}
      {detailLoading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">{pa('loadingConfiguration')}</Text>
            </div>
          </div>
        </Card>
      ) : (
        <>
          {!selectedConfig && configurations.length === 0 && (
            <Alert
              message={pa('noConfigSelected')}
              description={pa('selectOrCreateConfig')}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabs}
            type="card"
            size="large"
          />
        </>
      )}
    </div>
  );
}
